/**
 * Auth boundary: who is using the app right now.
 *
 * Every component that needs the current user goes through `useViewer()`.
 * The session itself is an HttpOnly cookie the frontend never sees; the
 * viewer query (`GET /api/v1/auth/me`) tells whether it is valid and returns
 * the CSRF token, which is kept in memory only.
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { api, isApiError, queryKeys, setCsrfToken } from '@/shared/api';
import { changeLocale, i18n, type Locale } from '@/shared/i18n';
import {
  resolveBindings,
  type ShortcutBindings,
  type ShortcutOverrides,
} from '@/shared/lib/shortcuts';
import { applyTheme, storedPreference, type ThemePreference } from '@/shared/lib/theme';
import { applyScale, UI_SCALE_DEFAULT } from '@/shared/lib/ui-scale';

import type { AuthResponse, User } from './types';

export type ViewerState =
  { status: 'loading' } | { status: 'anonymous' } | { status: 'authenticated'; user: User };

async function fetchViewer(signal: AbortSignal): Promise<AuthResponse | null> {
  try {
    const auth = await api.get<AuthResponse>('/v1/auth/me', { signal });
    setCsrfToken(auth.csrf_token);
    applyUserLocale(auth.user);
    return auth;
  } catch (error) {
    if (isApiError(error) && error.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw error;
  }
}

export function useViewer(): ViewerState {
  const query = useQuery({
    queryKey: queryKeys.viewer,
    queryFn: ({ signal }) => fetchViewer(signal),
    staleTime: 5 * 60_000,
  });
  if (query.isPending) return { status: 'loading' };
  // A failed check (network/5xx) is treated as anonymous; guarded pages retry on navigation.
  if (!query.data) return { status: 'anonymous' };
  return { status: 'authenticated', user: query.data.user };
}

/** The account's language wins over the browser/local choice once signed in. */
function applyUserLocale(user: User): void {
  if (i18n.language !== user.locale) void changeLocale(user.locale);
}

/** After login/register: remember the session without another round-trip. */
export function setViewer(queryClient: QueryClient, auth: AuthResponse): void {
  setCsrfToken(auth.csrf_token);
  applyUserLocale(auth.user);
  queryClient.setQueryData(queryKeys.viewer, auth);
}

/** After a profile update: keep the cached user in sync. */
export function updateViewerUser(queryClient: QueryClient, user: User): void {
  applyUserLocale(user);
  queryClient.setQueryData<AuthResponse | null>(queryKeys.viewer, (current) =>
    current ? { ...current, user } : current,
  );
}

/** After logout or session expiry: drop the session and every cached private query. */
export function clearViewer(queryClient: QueryClient): void {
  setCsrfToken(null);
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== queryKeys.viewer[0] });
  queryClient.setQueryData(queryKeys.viewer, null);
}

/**
 * Applies the signed-in user's display scale to the whole app (CSS custom properties on <html>).
 * Called once by the authenticated shell; the settings page previews changes immediately with
 * `applyScale` and the saved value arrives here through the viewer query.
 */
export function useApplyUiScale(): void {
  const viewer = useViewer();
  const scale = viewer.status === 'authenticated' ? viewer.user.ui_scale : UI_SCALE_DEFAULT;
  useEffect(() => {
    applyScale(scale);
  }, [scale]);
}

/** Saves the display scale (percent) to the account; the cached viewer follows the server copy. */
export function useSaveUiScale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (uiScale: number) => api.patch<User>('/v1/account', { ui_scale: uiScale }),
    onSuccess: (user) => {
      updateViewerUser(client, user);
    },
  });
}

/**
 * Keeps `<html data-theme>` in step with the account preference (the stored copy before the
 * viewer is known) and, for "system", with the OS while the app is open.
 */
export function useApplyTheme(): void {
  const viewer = useViewer();
  const preference: ThemePreference =
    viewer.status === 'authenticated' ? viewer.user.theme : storedPreference();
  useEffect(() => {
    applyTheme(preference);
    if (preference !== 'system' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      applyTheme('system');
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, [preference]);
}

/** Saves the theme preference to the account; it is applied at once (and put back on failure). */
export function useSaveTheme() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (theme: ThemePreference) => api.patch<User>('/v1/account', { theme }),
    onSuccess: (user) => {
      updateViewerUser(client, user);
    },
  });
}

/**
 * The UI language: switching applies at once and, when signed in, is saved to the account (the
 * server never blocks the switch: a failed save simply retries on the next change).
 */
export function useLocalePreference() {
  const viewer = useViewer();
  const client = useQueryClient();
  return (locale: Locale) => {
    void changeLocale(locale);
    if (viewer.status === 'authenticated' && viewer.user.locale !== locale) {
      api.patch<User>('/v1/account', { locale }).then(
        (user) => {
          updateViewerUser(client, user);
        },
        () => undefined,
      );
    }
  };
}

/** The effective shortcut bindings: defaults overlaid with the account's overrides. */
export function useShortcutBindings(): ShortcutBindings {
  const viewer = useViewer();
  const overrides = viewer.status === 'authenticated' ? viewer.user.shortcuts : undefined;
  return useMemo(() => resolveBindings(overrides), [overrides]);
}

/** Replaces the account's shortcut overrides (the whole map). */
export function useSaveShortcuts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (shortcuts: ShortcutOverrides) => api.patch<User>('/v1/account', { shortcuts }),
    onSuccess: (user) => {
      updateViewerUser(client, user);
    },
  });
}

/** Master switch of the shortcuts (the stored bindings are untouched either way). */
export function useShortcutsEnabled(): boolean {
  const viewer = useViewer();
  return viewer.status !== 'authenticated' || viewer.user.shortcuts_enabled;
}

export function useSaveShortcutsEnabled() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (shortcuts_enabled: boolean) =>
      api.patch<User>('/v1/account', { shortcuts_enabled }),
    onSuccess: (user) => {
      updateViewerUser(client, user);
    },
  });
}
